import { Route, Routes, Navigate } from 'react-router-dom'
import LoginPage from './pages/auth/LoginPage'
import SignupPage from './pages/auth/SignupPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import PasswordResetPage from './pages/auth/PasswordResetPage'
import AuthCallbackHandler from '@/pages/auth/AuthCallbackHandler'
import { BackofficeGuard, AppGuard } from './providers/auth/RoleGuards'
import { AppLoadingIndicator } from './components/ui/AppLoadingIndicator'
import MobileNotSupportedPage from './pages/auth/MobileNotSupportedPage'

import UserDashboard from './pages/dashboard/UserDashboard'
import { SettingsPanel } from './pages/settings/SettingsPanel'

// Placeholder pages
import UnionDashboard from './pages/dashboard/UnionDashboard'
import { BoDashboard } from './pages/dashboard/BoDashboard'

import { ROLES } from '@/constants/roles.constants'
import { useUserStore } from './stores/user.store'
// import { InvoiceSettings } from './pages/settings/InvoiceSettings'
import { OrderCapacitySettings } from './pages/settings/OrderCapacitySettings'
import { BankAdvisorSettings } from './pages/settings/BankAdvisorSettings'
import BankSettings from './pages/settings/BankSettings'
import { BankSettingsShell } from './pages/settings/BankSettingsShell'
import { ProfileSettingsPage } from './pages/settings/ProfileSettingsPage'
import { ProfileSettingsShell } from './pages/settings/profile/ProfileSettingsShell'
import { ProfileDataPage } from './pages/settings/profile/ProfileDataPage'
import { NotificationSettings } from './pages/settings/NotificationSettings'
import { ContractSettings } from './pages/settings/subscription/ContractSettings'
import { OnboardingWizard } from './components/onboarding/OnboardingWizard'
import NotFoundPage from './pages/NotFoundPage'
import { InvoiceSettingsShell } from './pages/settings/invoices/InvoiceSettingsShell'
import { InvoiceSettingsDataPage } from './pages/settings/invoices/InvoiceSettingsDataPage'
import { InvoiceSettingsAutomationPage } from './pages/settings/invoices/InvoiceSettingsAutomationPage'
import { InvoiceSettingsNumberingPage } from './pages/settings/invoices/InvoiceSettingsNumberingPage'
import { InvoiceSettingsTextsPage } from './pages/settings/invoices/InvoiceSettingsTextsPage'
import { BackendUnavailableOverlay } from './pages/BackendUnavailableOverlay'
import CreateActivityPage from './components/activity/CreateActivityPage'
import { SubscriptionSettingsShell } from './pages/settings/subscription/SubscriptionSettingsShell'
import { SubscriptionBillingDataPage } from './pages/settings/subscription/SubscriptionBillingDataPage'
import { GdprExportPage } from './pages/settings/subscription/GdprExportPage'
import { ServiceBillingInvoiceReviewPage } from './pages/union/service-billing/components/ServiceBillingInvoiceReviewPage'
import { UnionDemandListPage } from '@/features/marketplace/pages/UnionDemandListPage'
import { CreateDemandPage } from '@/features/marketplace/pages/CreateDemandPage_obsolete'
import { DemandDetailPage } from '@/features/marketplace/pages/DemandDetailPage'
import { MatchingResultsPage } from '@/features/marketplace/pages/MatchingResultsPage'
import { InterestedSibsPage } from '@/features/marketplace/pages/InterestedSibsPage'
import { MarketplaceListPage } from '@/features/marketplace/pages/MarketplaceListPage'
import { MarketplaceDemandDetailPage } from '@/features/marketplace/pages/MarketplaceDemandDetailPage'
import { UnionDemandDetailPage } from '@/features/marketplace/pages/UnionDemandDetailPage'
import { UnionMarketplacePage } from '@/features/marketplace/pages/UnionMarketplacePage'
import { UnionOrdersPage } from '@/pages/union/order-overview/UnionOrdersPage'
import { CapacityManagementDashboard } from '@/pages/union/capacity/CapacityManagementDashboard'
import { ServiceBillingDashboard } from '@/pages/union/service-billing/components/ServiceBillingDashboard'
import UnionExportPage from '@/components/union-orders/UnionExportPage'
import UnionBanksManagement from '@/pages/union/settings/UnionBanksManagement'
import { UNION_MANAGER_ROUTES } from '@/constants/unionRoutes.constants'
import { UserPortalLayout } from '@/pages/dashboard/UserPortalLayout'

function App() {
  const user = useUserStore(s => s.user)
  const loading = useUserStore(s => s.loading)
  const sessionRestored = useUserStore(s => s.sessionRestored)

  return (
    <>
      <BackendUnavailableOverlay />
      <OnboardingWizard />
      <Routes>
        {/* Root route: redirect depending on role */}
        <Route
          path="/"
          element={
            !sessionRestored || loading ? (
              <AppLoadingIndicator />
            ) : !user ? (
              <Navigate to="/login" replace />
            ) : (
              <Navigate
                to={
                  user.role === ROLES.ADMIN
                    ? '/bo/dashboard'
                    : user.role === ROLES.MANAGER
                      ? UNION_MANAGER_ROUTES.orders
                      : '/app/user/dashboard'
                }
                replace
              />
            )
          }
        />

        {/* PUBLIC ROUTES */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/bo/login" element={<LoginPage />} />
        <Route path="/register" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<PasswordResetPage />} />
        <Route path="/auth/callback" element={<AuthCallbackHandler />} />
        <Route path="/mobile-not-supported" element={<MobileNotSupportedPage />} />

        {/* BACKOFFICE */}
        <Route
          path="/bo/dashboard"
          element={
            <BackofficeGuard>
              <BoDashboard />
            </BackofficeGuard>
          }
        />

        {/* APP: SIB */}
        <Route
          path="/app/user/dashboard"
          element={
            <AppGuard allowedRoles={[ROLES.USER, ROLES.ADMIN]}>
              <UserDashboard />
            </AppGuard>
          }
        ></Route>
        <Route
          path="/activities/new"
          element={
            <AppGuard allowedRoles={[ROLES.USER]}>
              <CreateActivityPage key="activity-new" />
            </AppGuard>
          }
        />
        <Route
          path="/activities/:id/edit"
          element={
            <AppGuard allowedRoles={[ROLES.USER]}>
              <CreateActivityPage key="activity-edit" />
            </AppGuard>
          }
        />

        {/* APP: UNION — section paths mirror UNION_MANAGER_ROUTES */}
        <Route
          path="/app/manager"
          element={
            <AppGuard allowedRoles={[ROLES.MANAGER, ROLES.ADMIN]}>
              <UnionDashboard />
            </AppGuard>
          }
        >
          <Route index element={<Navigate to={UNION_MANAGER_ROUTES.orders} replace />} />
          <Route
            path="dashboard"
            element={<Navigate to={UNION_MANAGER_ROUTES.orders} replace />}
          />
          <Route path="orders" element={<UnionOrdersPage />} />
          <Route path="capacity" element={<CapacityManagementDashboard />} />
          <Route path="marketplace" element={<UnionMarketplacePage />} />
          <Route path="service-billing" element={<ServiceBillingDashboard />} />
          <Route
            path="service-billing/invoices/:invoiceId"
            element={<ServiceBillingInvoiceReviewPage />}
          />
          <Route path="export" element={<UnionExportPage />} />
          <Route path="banks" element={<UnionBanksManagement />} />
        </Route>

        {/* Settings */}
        <Route
          path="/settings"
          element={
            <AppGuard allowedRoles={[ROLES.USER, ROLES.MANAGER, ROLES.ADMIN]}>
              <SettingsPanel />
            </AppGuard>
          }
        >
          {/* <Route path="invoice" element={<InvoiceSettings />} /> */}
          <Route path="/settings/invoice" element={<InvoiceSettingsShell />}>
            <Route index element={<InvoiceSettingsDataPage />} />
            <Route path="automation" element={<InvoiceSettingsAutomationPage />} />
            <Route path="numbering" element={<InvoiceSettingsNumberingPage />} />
            <Route path="texts" element={<InvoiceSettingsTextsPage />} />
          </Route>
          <Route path="orders" element={<OrderCapacitySettings />} />
          <Route path="bank" element={<BankSettingsShell />}>
            <Route index element={<BankSettings />} />
            <Route path="advisors" element={<BankAdvisorSettings />} />
          </Route>
          <Route path="/settings/profile" element={<ProfileSettingsShell />}>
            <Route index element={<ProfileDataPage />} />
            <Route path="account" element={<ProfileSettingsPage />} />
          </Route>
          {/* <Route path="contract" element={<ContractSettings />} /> */}
          <Route path="/settings/subscription" element={<SubscriptionSettingsShell />}>
            <Route index element={<SubscriptionBillingDataPage />} />
            <Route path="contract" element={<ContractSettings />} />
            <Route path="gdpr-export" element={<GdprExportPage />} />
          </Route>
          <Route path="notifications" element={<NotificationSettings />} />
        </Route>
        {/* Marketplace: SIB */}
        {/* <Route
          path="/marketplace"
          element={
            <AppGuard allowedRoles={[ROLES.USER, ROLES.ADMIN]}>
              <MarketplaceListPage />
            </AppGuard>
          }
        />

        <Route
          path="/marketplace/:demandId"
          element={
            <AppGuard allowedRoles={[ROLES.USER, ROLES.ADMIN]}>
              <MarketplaceDemandDetailPage />
            </AppGuard>
          }
        /> */}

        <Route
          path="/app/user"
          element={
            <AppGuard allowedRoles={[ROLES.USER, ROLES.ADMIN]}>
              <UserPortalLayout />
            </AppGuard>
          }
        >
          <Route path="marketplace" element={<MarketplaceListPage />} />
          <Route path="marketplace/:demandId" element={<MarketplaceDemandDetailPage />} />
        </Route>

        <Route
          path="/union/demands/new"
          element={
            <AppGuard allowedRoles={[ROLES.MANAGER, ROLES.ADMIN]}>
              <CreateDemandPage />
            </AppGuard>
          }
        />

        {/* <Route
          path="/union/demands/:demandId"
          element={
            <AppGuard allowedRoles={[ROLES.MANAGER, ROLES.ADMIN]}>
              <DemandDetailPage />
            </AppGuard>
          }
        /> */}

        <Route
          path="/union/demands/:demandId/matching"
          element={
            <AppGuard allowedRoles={[ROLES.MANAGER, ROLES.ADMIN]}>
              <MatchingResultsPage />
            </AppGuard>
          }
        />

        <Route
          path="/union/demands/:demandId/interested-sibs"
          element={
            <AppGuard allowedRoles={[ROLES.MANAGER, ROLES.ADMIN]}>
              <InterestedSibsPage />
            </AppGuard>
          }
        />
        <Route
          path="/union/demands/:demandId"
          element={
            <AppGuard allowedRoles={[ROLES.MANAGER, ROLES.ADMIN]}>
              <UnionDemandDetailPage />
            </AppGuard>
          }
        />
        {/* 404 – must be LAST */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  )
}

export default App
